import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { createNach } from "../../../services/sanctions";
import { DataContext } from "../../../context/DataContext";
import { DataContextType } from "../../../types/DataContextType";
import {
  AgreementStatus,
  DigilockerStatus,
  MandateStatus,
  SelfieStatus,
} from "../../../utils/config";
import { MandateType } from "../../../utils/types";
import { useStageGuard } from "../useStageGuard";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import GreenTick from "../../../images/icons/sanctions-icons/green-tick.svg";
import GreyDot from "../../../images/icons/sanctions-icons/grey-dot.svg";
import eligStyles from "../index.module.css";

let Digio: any;

const STEPS = [
  { key: "digilocker", label: "Digilocker KYC" },
  { key: "selfie", label: "Selfie" },
  { key: "agreement", label: "Agreement" },
  { key: "mandate", label: "e-Mandate" },
];

function EligibilitySanctions() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  useStageGuard(userData?.userId, userData?.applicationId, 9);

  const { redirectUrl } = useContext(DataContext) as DataContextType;

  const [mandateStatus, setMandateStatus] = useState<MandateType>({});
  const [authToken, setAuthToken] = useState({ mob: "", value: "" });
  const [flag, setFlag] = useState(false);
  const [loadingMandate, setLoadingMandate] = useState(false);
  const [loadingAgreement, setLoadingAgreement] = useState(false);

  const selfieData = sessionStorage.getItem("data");

  useEffect(() => {
    if (!userData?.userId) return;
    const eligToken = sessionStorage.getItem("eligibility_auth_token");
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/generate-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(eligToken ? { Authorization: `Bearer ${eligToken}` } : {}),
      },
      body: JSON.stringify({
        mobile: userData?.mobile,
        userId: userData?.userId,
        applicationId: userData?.applicationId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAuthToken({ mob: userData?.mobile, value: data.data });
        setInterval(() => {
          fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/auth`, {
            headers: {
              Authorization: `Bearer ${data.data}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
          })
            .then((res) => res.json())
            .then((res) => setMandateStatus(res.data));
        }, 2000);
      });
  }, [flag, userData?.userId]);

  const fetchJsFromCDN = (src: any, externals: string[]) => {
    externals = !externals ? [] : externals;
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.setAttribute("src", src);
      script.addEventListener("load", () => {
        resolve(externals.map((key: any) => (window as any)[key]));
      });
      script.addEventListener("error", reject);
      document.body.appendChild(script);
    });
  };

  const reload = () => {
    setFlag((f) => !f);
    navigate("/eligibility/sanctions", { state: { data: userData } });
  };

  function digilockerHandler() {
    fetchJsFromCDN(process.env.REACT_APP_DIGIO_SDK, ["Digio"]).then((digio: any) => {
      fetch(`${process.env.REACT_APP_DASHBOARD_URL}/digilocker/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken.value}` },
        redirect: "follow",
      })
        .then((res) => res.json())
        .then((result) => {
          const { kid, tokenId } = result.data;
          Digio = digio[0];
          const d = new Digio({
            environment: process.env.REACT_APP_DIGIO_ENV,
            logo: "yourlogourl",
            theme: { primaryColor: "#234FDA", secondaryColor: "#234FDA" },
            is_iframe: true,
            callback: (_digio: any) => {
              if (_digio.error_code !== undefined && _digio.error_code !== "CANCELLED") {
                reload();
                throw new Error(_digio.message);
              }
              reload();
            },
          });
          d.init();
          d.submit(kid, authToken.mob, tokenId);
        })
        .catch((e) => console.log("digilocker error", e));
    });
  }

  function selfieHandler() {
    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/selfie/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken.value}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.message === "success") {
          window.open(
            `${process.env.REACT_APP_DIGIO_BASEURL}/#/gateway/login/${res.data.entityId}/vI3atY/${userData?.mobile}?token_id=${res.data.tokenId}&redirect_url=${process.env.REACT_APP_FRONTEND_URL}/${selfieData}`,
            "_self",
            "noopener,noreferrer"
          );
        }
      });
  }

  function loanAgreementHandler() {
    setLoadingAgreement(true);
    fetchJsFromCDN(process.env.REACT_APP_DIGIO_SDK, ["Digio"])
      .then((digio: any) => {
        fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/agreement/${
            mandateStatus?.isCoapplicant ? "coapplicant-generate" : "generate"
          }`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${authToken.value}` },
            redirect: "follow",
          }
        )
          .then((res) => res.json())
          .then((result) => {
            const { email, id, tokenId } = result.data;
            const D = digio[0];
            const d = new D({
              environment: process.env.REACT_APP_DIGIO_ENV,
              logo: "yourlogourl",
              theme: { primaryColor: "#234FDA", secondaryColor: "#234FDA" },
              digioDocumentId: id,
              digioUserIdentifier: email,
              is_iframe: true,
              callback: (_digio: any) => {
                setLoadingAgreement(false);
                if (_digio.error_code !== undefined && _digio.error_code !== "CANCELLED") {
                  reload();
                  throw new Error(_digio.message);
                }
                reload();
              },
            });
            d.init();
            d.submit(id, email, tokenId);
          })
          .catch((e) => { setLoadingAgreement(false); console.log("agreement error", e); });
      })
      .catch((e) => { setLoadingAgreement(false); console.log("digio load error", e); });
  }

  async function getRazorpayUrl() {
    setLoadingMandate(true);
    try {
      const response = await createNach({
        userId: userData?.userId,
        applicationId: userData?.applicationId,
      });
      window.open(response?.data?.authLink, "_self", "noopener,noreferrer");
    } catch (e) {
      console.error("mandate error", e);
    } finally {
      setLoadingMandate(false);
    }
  }

  // ── Step status helpers ──────────────────────────────────────────────────
  const isDone = (key: string) => {
    if (key === "digilocker") return mandateStatus?.kycDone || mandateStatus?.digilockerStatus === DigilockerStatus.Successful;
    if (key === "selfie") return mandateStatus?.selfieStatus === SelfieStatus.Successful;
    if (key === "agreement") return mandateStatus?.agreementStatus === AgreementStatus.Signed;
    if (key === "mandate") return mandateStatus?.enachStatus === MandateStatus?.Successful;
    return false;
  };

  const isActive = (key: string) => {
    if (key === "digilocker") return !isDone("digilocker");
    if (key === "selfie") {
      if (isDone("selfie")) return false;
      return isDone("digilocker") || mandateStatus?.digilockerStatus === DigilockerStatus.Successful;
    }
    if (key === "agreement") {
      if (isDone("agreement")) return false;
      if (loadingAgreement) return false;
      if (mandateStatus?.selfieStatus === SelfieStatus.Failed) return true;
      return isDone("selfie");
    }
    if (key === "mandate") {
      if (isDone("mandate")) return false;
      if (loadingMandate) return false;
      if (mandateStatus?.agreementStatus === AgreementStatus.PendingCoapplicant) return true;
      return isDone("agreement");
    }
    return false;
  };

  const handleStep = (key: string) => {
    if (key === "digilocker") digilockerHandler();
    if (key === "selfie") selfieHandler();
    if (key === "agreement") loanAgreementHandler();
    if (key === "mandate") getRazorpayUrl();
  };

  return (
    <div className={eligStyles.body}>
      <div className={eligStyles.backdrop}>
        <div className={eligStyles.main}>
          <Navbar />
          <div className={eligStyles.cardWrapper}>
            <div className={eligStyles.container}>

              <div className={eligStyles.cardHeader}>
                <p className={eligStyles.cardTitle}>KYC &amp; Documents</p>
              </div>

              <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: 0 }}>
                {STEPS.map((step, idx) => {
                  const done = isDone(step.key);
                  const active = isActive(step.key);
                  const isLast = idx === STEPS.length - 1;
                  const loading = (step.key === "agreement" && loadingAgreement) || (step.key === "mandate" && loadingMandate);
                  // hide selfie row when selfieStatus is Failed
                  if (step.key === "selfie" && mandateStatus?.selfieStatus === SelfieStatus.Failed) return null;

                  return (
                    <div key={step.key}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.85rem 1rem",
                        background: done ? "#f6fdf6" : active ? "#fffcfa" : "#fafafa",
                        border: `1px solid ${done ? "#b6e8bb" : active ? "#f9d8d6" : "#efefef"}`,
                        borderRadius: "10px",
                        marginBottom: isLast ? 0 : "0.6rem",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <img
                            src={done ? GreenTick : GreyDot}
                            alt=""
                            style={{ width: "1.6rem", height: "1.6rem" }}
                          />
                          <span style={{
                            fontSize: "0.92rem",
                            fontWeight: 600,
                            color: done ? "#2e7d32" : active ? "#222" : "#aaa",
                          }}>
                            {step.label}
                          </span>
                        </div>

                        {done && (
                          <span style={{ fontSize: "0.75rem", color: "#2e7d32", fontWeight: 600 }}>Done</span>
                        )}
                        {!done && active && (
                          <button
                            onClick={() => handleStep(step.key)}
                            disabled={loading}
                            style={{
                              padding: "0.35rem 1.1rem",
                              background: loading ? "#aaa" : "#d32028",
                              color: "#fff",
                              border: "none",
                              borderRadius: "60px",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              fontFamily: "Outfit, sans-serif",
                              cursor: loading ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              width: "fit-content",
                              display: "inline-block",marginTop:'-0.1rem'
                            }}
                          >
                            {loading ? "Loading..." : "Start"}
                          </button>
                        )}
                        {!done && !active && (
                          <span style={{ fontSize: "0.72rem", color: "#ccc" }}>Pending</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p style={{ fontSize: "0.7rem", color: "#aaa", marginTop: "1rem", marginBottom: 0, textAlign: "center" }}>
                Powered by{" "}
                <img src="https://www.digio.in/images/digio_blue.png" alt="Digio" style={{ height: "0.9rem", verticalAlign: "middle", marginLeft: "0.25rem" }} />
              </p>
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilitySanctions;
